import { useEffect, useRef, useState } from "react";
import { PopOutButton } from "@/components/dashboard/PopOut";
import { toast } from "sonner";
import { openExternal } from "@/lib/redirect";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "@/lib/api";
import { PaperPlaneRight, Sparkle, ArrowLeft, Plus, ClockCounterClockwise } from "@phosphor-icons/react";
import { useCalibrator } from "@/components/dashboard/useCalibrator";
import { ChatList } from "@/components/dashboard/ChatList";

const PROMPTS = [
  "What if I hire two engineers at $150k in Q3?",
  "What if we lose our biggest customer next month?",
  "What if we raise a $1.5M seed round in 3 months?",
];

const HeaderBtn = ({ testid, onClick, disabled, children }) => (
  <button data-testid={testid} onClick={onClick} disabled={disabled}
    className="flex items-center gap-1 rounded-full border border-[#2A2A2A] px-3 py-1 text-xs hover:border-[#9D00FF] hover:text-[#D9A6FF] transition-colors disabled:opacity-40">{children}</button>
);

const MODELS = [["claude", "Claude Opus 5.5"], ["gemini", "Gemini 3.1 Pro"]];

const ModelPicker = ({ model, setModel, locked, usage }) => {
  const api = useApi();
  const { data } = useQuery({ queryKey: ["ai-models"], queryFn: () => api.get("/ai/models"), staleTime: Infinity });
  const models = data ? data.map((m) => [m.id, m.label]) : MODELS;
  return (
  <div data-testid="calibrator-model-picker" className="px-5 py-2 border-b border-[#2A2A2A] flex items-center gap-2 text-[11px]">
    <span className="font-mono text-[#A3A3A3]">MODEL</span>
    {models.map(([id, label]) => (
      <button key={id} data-testid={`calibrator-model-${id}`} disabled={locked && model !== id} onClick={() => !locked && setModel(id)}
        className={`rounded-full px-3 py-1 transition-colors ${model === id ? "bg-[#9D00FF] text-white" : "border border-[#2A2A2A] text-[#A3A3A3] hover:border-[#9D00FF]/60"} disabled:opacity-30 disabled:cursor-not-allowed`}>{label}</button>
    ))}
    {locked && <span className="text-[#6B6B6B]">set for this chat</span>}
    {usage && <span data-testid="calibrator-usage" className={`ml-auto font-mono ${usage.left <= 3 ? "text-[#FFB800]" : "text-[#A3A3A3]"}`}>{usage.left}/{usage.limit} left today</span>}
  </div>
  );
};

const LimitBanner = ({ usage }) => {
  const api = useApi();
  const [busy, setBusy] = useState(false);
  const upgrade = async () => {
    setBusy(true);
    try {
      await openExternal(async () => (await api.post("/payments/checkout", { lookup_key: "pro_monthly", origin_url: window.location.origin })).checkout_url);
    } catch { toast.error("Couldn't open checkout. Try again from Billing."); }
    setBusy(false);
  };
  return (
    <div data-testid="calibrator-limit-banner" className="mx-3 mb-0 mt-2 rounded-lg border border-[#FFB800]/40 bg-[#FFB800]/[0.07] px-4 py-3 text-xs leading-relaxed">
      <p className="font-medium text-[#FFB800]">That's all {usage.limit} calibrations for today.</p>
      <p className="text-[#A3A3A3] mt-0.5">Nice work stress-testing your runway. Your questions refill at midnight UTC, so come back tomorrow.</p>
      {usage.plan !== "pro" && (
        <button data-testid="calibrator-upgrade-btn" onClick={upgrade} disabled={busy}
          className="mt-2 rounded-full bg-[#00FF9D] px-4 py-1.5 font-semibold text-[#050505] hover:shadow-[0_0_16px_rgba(0,255,157,0.5)] transition-shadow disabled:opacity-50">
          {busy ? "Opening checkout…" : "Upgrade to Pro: 100 a day"}
        </button>
      )}
    </div>
  );
};

const Message = ({ m, i, onScenario, onSave }) => (
  <div data-testid={`sim-msg-${m.role}`} className={m.role === "user" ? "flex justify-end" : ""}>
    <div className={`max-w-[92%] rounded-xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[#1A1A1A]" : "border border-[#9D00FF]/30 bg-[#9D00FF]/[0.07]"}`}>
      <span className={m.streaming ? "caret" : ""}>{m.content}</span>
      {m.scenario && (
        <div className="mt-2 flex items-center gap-3">
          <button data-testid={`sim-plot-${i}`} onClick={() => onScenario(m.scenario)} className="font-mono text-[11px] text-[#D9A6FF] hover:underline">→ plot: {m.scenario.label}</button>
          <button data-testid={`sim-save-${i}`} onClick={() => onSave(m.scenario)} className="font-mono text-[11px] text-[#00FF9D] hover:underline">save scenario</button>
        </div>
      )}
    </div>
  </div>
);

const Suggestions = ({ submit }) => (
  <div className="space-y-2">
    <p className="text-sm text-[#A3A3A3] mb-3">Ask a what-if. I'll recalibrate your runway.</p>
    {PROMPTS.map((p, i) => (
      <button key={p} data-testid={`sim-suggestion-${i}`} onClick={() => submit(p)}
        className="w-full text-left rounded-lg border border-[#2A2A2A] px-3 py-2.5 text-sm hover:border-[#9D00FF]/60 hover:bg-[#9D00FF]/10 transition-colors">{p}</button>
    ))}
  </div>
);

export const Calibrator = ({ onScenario, onSave }) => {
  const [q, setQ] = useState("");
  const [showList, setShowList] = useState(false);
  const c = useCalibrator(onScenario);
  const box = useRef(null);
  useEffect(() => { if (box.current) box.current.scrollTop = box.current.scrollHeight; }, [c.msgs, showList]);
  const ask = (t) => { setShowList(false); c.ask(t); };
  const api = useApi();
  const { data: usage } = useQuery({ queryKey: ["ai-usage"], queryFn: () => api.get("/ai/usage") });
  const out = usage ? usage.left <= 0 : false;
  const submit = (text) => { const t = (text ?? q).trim(); if (!t || c.busy || out) return; setQ(""); ask(t); };

  return (
    <div data-testid="ai-calibrator" className="ai-beam rounded-xl border border-[#2A2A2A] bg-[#0D0A12] flex flex-col h-[560px]">
      <div className="px-5 py-4 border-b border-[#2A2A2A] flex items-center gap-2">
        {showList
          ? <HeaderBtn testid="calibrator-back-btn" onClick={() => setShowList(false)}><ArrowLeft size={12} /> Back</HeaderBtn>
          : <Sparkle weight="fill" className="text-[#9D00FF]" />}
        <h3 className="font-display text-lg flex-1 truncate">AI Scenario Calibrator</h3>
        {!showList && <HeaderBtn testid="calibrator-history-btn" onClick={() => setShowList(true)} disabled={c.busy}><ClockCounterClockwise size={12} /> Chats</HeaderBtn>}
        <HeaderBtn testid="calibrator-new-chat-btn" onClick={() => { c.newChat(); setShowList(false); }} disabled={c.busy}><Plus size={12} /> New</HeaderBtn>
        <PopOutButton />
      </div>
      {!showList && <ModelPicker model={c.model} setModel={c.setModel} locked={!!c.sessionId || c.busy} usage={usage} />}
      <div ref={box} className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="simulator-messages">
        {showList ? (
          <ChatList current={c.sessionId} onOpen={async (id) => { await c.open(id); setShowList(false); }}
            onDeleted={(id) => id === c.sessionId && c.newChat()} />
        ) : c.loading ? <p className="text-sm text-[#A3A3A3]">Loading conversation…</p> : (
          <>
            {c.msgs.length === 0 && !out && <Suggestions submit={submit} />}
            {c.msgs.map((m, i) => <Message key={m.id || i} m={m} i={i} onScenario={onScenario} onSave={onSave} />)}
          </>
        )}
      </div>
      {out && <LimitBanner usage={usage} />}
      <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="p-3 border-t border-[#2A2A2A] flex gap-2">
        <input data-testid="runway-simulator-input" value={q} onChange={(e) => setQ(e.target.value)} disabled={c.busy || out}
          placeholder={out ? "Daily limit reached. Back tomorrow." : "What if we cut marketing by half?"}
          className="flex-1 rounded-full bg-[#050505] border border-[#2A2A2A] px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#9D00FF] focus:outline-none placeholder:text-[#555]" />
        <button data-testid="runway-simulator-submit" disabled={c.busy || !q.trim() || out}
          className="rounded-full bg-[#9D00FF] px-4 text-white disabled:opacity-40 hover:shadow-[0_0_20px_rgba(157,0,255,0.6)] transition-shadow">
          <PaperPlaneRight weight="fill" />
        </button>
      </form>
    </div>
  );
};
